import { Grid, Link } from "@mui/material";
import { FormikProps } from "formik";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import {
  ACKNOWLEDGEMENT_FIELDS,
  AUDIO_ONLY_REASON_FIELD,
  CCM_CONSENT_STYLE,
  CCM_CONSENT_TEXT,
  EMAIL_LINK_PREFIX,
  OTHER_MODALITY_FIELD,
  SIGNATURE_DATE_FIELD,
  VISIT_MODALITY,
} from "../ccm-consent-constant";
import {
  CONSENT_CHECKBOX_TEXT,
  ELECTRONIC_CONSENT_LINES,
  PRIVACY_NOTICE_SECTIONS,
  TELEHEALTH_BLOCKS,
  VERBAL_CONSENT_LINES,
  VISIT_MODALITY_OPTIONS,
} from "../ccm-consent-copy-constant";
import {
  CcmConsentFormValues,
  PracticeContact,
  VisitModality,
} from "../ccm-consent-types";
import {
  ConsentCheckbox,
  ConsentField,
  ConsentFieldGrid,
  ConsentLines,
  ConsentSection,
  ConsentTextBlocks,
} from "./ConsentFormFields";
import ConsentSignaturePad from "./ConsentSignaturePad";
import { getConsentSignatureError } from "../ccm-consent-utils";

export interface CcmConsentSectionsProps {
  formik: FormikProps<CcmConsentFormValues>;
  practiceContact: PracticeContact;
}

const CcmConsentSections = ({
  formik,
  practiceContact,
}: CcmConsentSectionsProps) => {
  const { telehealth, consentDocumentation } = formik.values;

  const selectModality = (modality: VisitModality, isChecked: boolean) => {
    formik.setFieldValue("telehealth.visitModality", isChecked ? modality : "");
    if (!isChecked || modality !== VISIT_MODALITY.OTHER)
      formik.setFieldValue("telehealth.otherModality", "");
  };

  return (
    <>
      <ConsentSection title={CCM_CONSENT_TEXT.TELEHEALTH_CONSENT}>
        <ConsentCheckbox
          label={CONSENT_CHECKBOX_TEXT.TELEHEALTH_VERBAL}
          checked={telehealth.verbalConsent}
          onChange={(isChecked) =>
            formik.setFieldValue("telehealth.verbalConsent", isChecked)
          }
        />
        <ConsentTextBlocks blocks={TELEHEALTH_BLOCKS} />
        <Flex alignItems="center" flexWrap="wrap" columnGap={2}>
          <Text>{CCM_CONSENT_TEXT.VISIT_MODALITY}</Text>
          {VISIT_MODALITY_OPTIONS.map((option) => (
            <ConsentCheckbox
              key={option.value}
              label={option.label}
              checked={telehealth.visitModality === option.value}
              onChange={(isChecked) => selectModality(option.value, isChecked)}
            />
          ))}
        </Flex>
        <ConsentFieldGrid
          formik={formik}
          fields={
            telehealth.visitModality === VISIT_MODALITY.OTHER
              ? [OTHER_MODALITY_FIELD, AUDIO_ONLY_REASON_FIELD]
              : [AUDIO_ONLY_REASON_FIELD]
          }
        />
      </ConsentSection>

      <ConsentSection title={CCM_CONSENT_TEXT.CONSENT_DOCUMENTATION}>
        <ConsentCheckbox
          label={CONSENT_CHECKBOX_TEXT.ELECTRONIC_SMS}
          checked={consentDocumentation.electronicSmsConsent}
          onChange={(isChecked) =>
            formik.setFieldValue(
              "consentDocumentation.electronicSmsConsent",
              isChecked
            )
          }
        />
        <Text>{CCM_CONSENT_TEXT.UNABLE_TO_SIGN}</Text>
        <ConsentLines lines={ELECTRONIC_CONSENT_LINES} />
        <ConsentCheckbox
          label={CONSENT_CHECKBOX_TEXT.VERBAL}
          checked={consentDocumentation.verbalConsent}
          onChange={(isChecked) =>
            formik.setFieldValue(
              "consentDocumentation.verbalConsent",
              isChecked
            )
          }
        />
        <ConsentLines lines={VERBAL_CONSENT_LINES} />
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md="auto">
            <ConsentSignaturePad
              label={CCM_CONSENT_TEXT.PATIENT_SIGNATURE}
              required
              error={getConsentSignatureError(
                formik,
                "consentDocumentation.patientSignature"
              )}
              value={consentDocumentation.patientSignature}
              onChange={(signature) =>
                formik.setFieldValue(
                  "consentDocumentation.patientSignature",
                  signature
                )
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <ConsentField formik={formik} field={SIGNATURE_DATE_FIELD} />
          </Grid>
        </Grid>
      </ConsentSection>

      <PrivacyNoticeAcknowledgement
        formik={formik}
        practiceContact={practiceContact}
      />
    </>
  );
};

export default CcmConsentSections;

export interface PrivacyNoticeAcknowledgementProps<T> {
  formik: FormikProps<T>;
  practiceContact: PracticeContact;
}

/** PHI notice, practice contact and acknowledgement shared by every consent form. */
export const PrivacyNoticeAcknowledgement = <
  T extends Pick<CcmConsentFormValues, "acknowledgement">,
>({
  formik,
  practiceContact,
}: PrivacyNoticeAcknowledgementProps<T>) => {
  const { acknowledgement } = formik.values;

  return (
    <>
      {PRIVACY_NOTICE_SECTIONS.map(({ title, blocks }) => (
        <ConsentSection key={title} title={title}>
          <ConsentTextBlocks blocks={blocks} />
        </ConsentSection>
      ))}

      <ConsentSection title={CCM_CONSENT_TEXT.CONTACT_INFORMATION}>
        <Text fontStyle="italic">{CCM_CONSENT_TEXT.CONTACT_INTRO}</Text>
        <Flex flexDirection="column" gap={0.25}>
          <Text
            fontWeight={CCM_CONSENT_STYLE.HEADING_WEIGHT}
            color={CCM_CONSENT_STYLE.SUB_HEADING}
          >
            {practiceContact.name}
          </Text>
          <Text>{`${CCM_CONSENT_TEXT.PHONE} ${practiceContact.phone}`}</Text>
          <Text>{`${CCM_CONSENT_TEXT.FAX} ${practiceContact.fax}`}</Text>
          <Text>
            {CCM_CONSENT_TEXT.EMAIL}{" "}
            <Link href={`${EMAIL_LINK_PREFIX}${practiceContact.email}`}>
              {practiceContact.email}
            </Link>
          </Text>
          <Text>{`${CCM_CONSENT_TEXT.ADDRESS} ${practiceContact.address}`}</Text>
        </Flex>
      </ConsentSection>

      <Flex
        component="section"
        flexDirection="column"
        gap={2}
        p={1.5}
        sx={{
          border: 1,
          borderColor: CCM_CONSENT_STYLE.BORDER,
          borderRadius: 1,
          bgcolor: CCM_CONSENT_STYLE.HEADER_BG,
        }}
      >
        <Text>{CCM_CONSENT_TEXT.ACKNOWLEDGEMENT}</Text>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ConsentSignaturePad
              label={CCM_CONSENT_TEXT.ACK_PATIENT_SIGNATURE}
              required
              error={getConsentSignatureError(
                formik,
                "acknowledgement.patientSignature"
              )}
              value={acknowledgement.patientSignature}
              onChange={(signature) =>
                formik.setFieldValue(
                  "acknowledgement.patientSignature",
                  signature
                )
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ConsentSignaturePad
              label={CCM_CONSENT_TEXT.ACK_RESPONSIBLE_SIGNATURE}
              value={acknowledgement.responsiblePersonSignature}
              onChange={(signature) =>
                formik.setFieldValue(
                  "acknowledgement.responsiblePersonSignature",
                  signature
                )
              }
            />
          </Grid>
        </Grid>
        <ConsentFieldGrid formik={formik} fields={ACKNOWLEDGEMENT_FIELDS} />
        <Text fontStyle="italic" textAlign="center">
          {CCM_CONSENT_TEXT.UNABLE_TO_SIGN}
          <br />
          {CCM_CONSENT_TEXT.WITNESS_SIGNED}
        </Text>
      </Flex>
    </>
  );
};
